import React, { Component } from "react";
import { observer } from "mobx-react";
import PropTypes from "prop-types";
import { Button, ButtonGroup, Grid, Message } from "semantic-ui-react";
import Immersive3dTourFloorList from "./PanoramasSection/Immersive3dTourFloorList";
import Immersive3dTourFloor from "./PanoramasSection/Immersive3dTourFloor";
import Immersive3dTourAppPageStore from "~/stores/Immersive3dTourAppPageStore";
import ProcessingImageModal from "~/components/ProcessingImageModal";
import ProcessingImageStore from "~/stores/ProcessingImageStore";
import CustomDndProvider from "~/components/CustomDndProvider";
import ProcessingModal from "./ProcessingModal";

@observer
class PanoramasSection extends Component {
  /**
   * @property {ProcessingImageStore}
   */
  processingImageStore;

  constructor(props) {
    super(props);

    const { immersive3dTourAppPageStore } = this.props;
    const { immersive3dTourTabStore } = immersive3dTourAppPageStore;
    this.processingImageStore = new ProcessingImageStore(immersive3dTourTabStore);
    this.selectPanorama = this.selectPanorama.bind(this);
    this.onPanoramaDragBegin = this.onPanoramaDragBegin.bind(this);
    this.onBodyClick = this.onBodyClick.bind(this);
    this.onFloorDelete = this.onFloorDelete.bind(this);
  }

  componentDidMount() {
    const { immersive3dTourAppPageStore } = this.props;
    const { immersive3dTourTabStore } = immersive3dTourAppPageStore;

    immersive3dTourTabStore.initializeFloorsIfNeeded();
    document.body.addEventListener("click", this.onBodyClick);
  }

  componentWillUnmount() {
    document.body.removeEventListener("click", this.onBodyClick);
  }

  onBodyClick(event) {
    const {
      immersive3dTourAppPageStore: { immersive3dTourTabStore },
    } = this.props;

    if (!event.target.closest(".immersive3dTourPanorama")) {
      immersive3dTourTabStore.unselectAllPanoramas();
    }
  }

  onFloorHover(sourceItem, targetItem) {
    const {
      immersive3dTourAppPageStore: { immersive3dTourTabStore },
    } = this.props;

    if (!(sourceItem && targetItem)) {
      return;
    }

    // check if user is reordering floors
    if (!(sourceItem.type === "floor" && targetItem.type === "floor")) {
      return;
    }

    // Do not reorder if source and item reference the same floor.
    if (sourceItem.id === targetItem.id) {
      return;
    }

    immersive3dTourTabStore.reorderFloorsByIds(sourceItem.id, targetItem.id);
  }

  onFloorDelete(floorId, movePanosToFloorId = null) {
    const {
      immersive3dTourAppPageStore: { immersive3dTourTabStore },
    } = this.props;

    immersive3dTourTabStore.removeFloor(floorId, movePanosToFloorId);
  }

  onPanoramaDragBegin(item) {
    const {
      immersive3dTourAppPageStore: { immersive3dTourTabStore },
    } = this.props;

    const { floorsDisabled } = immersive3dTourTabStore;

    // if adding floors are disabled, then drag & drop has to be disabled too.
    if (floorsDisabled) {
      return;
    }

    // Since the "click" event won't fire when an item (pano) is being dragged, then we must set it ourselves.

    immersive3dTourTabStore.walkPanoramas(panorama => {
      // We'll only act on the matching panorama.
      if (panorama.id === item.id) {
        // if the panorama was selected already, then do nothing
        if (panorama.isSelected) {
          // break
          return true;
        }

        // panorama isSelected is false, then let's select it greedy.
        immersive3dTourTabStore.setPanoramaSelectedGreedy(item.id);
        // break
        return true;
      }

      return false;
    });

    // Let's track that the panorama is dragging.
    immersive3dTourTabStore.setDraggingPanoramaId(item.id);
  }

  onPanoramaDragEnd(sourceItem, targetItem) {
    const {
      immersive3dTourAppPageStore: { immersive3dTourTabStore },
    } = this.props;

    // Let's un-track the panorama that was being dragged.
    immersive3dTourTabStore.setDraggingPanoramaId(null);

    if (!(sourceItem && targetItem)) {
      return;
    }

    // We only accept a panorama to be dragged into a floor.
    if (!(sourceItem.type === "panorama" && targetItem.type === "floor")) {
      return;
    }

    // do nothing if the target floor is the same floor where the pano belongs to.
    if (sourceItem.floorId === targetItem.id) {
      return;
    }

    // get the list of selected panos, starting by the current dropped pano id.
    const selectedPanosId = [sourceItem.id];

    immersive3dTourTabStore.walkPanoramas(panorama => {
      if (panorama.isSelected && selectedPanosId.indexOf(panorama.id) === -1) {
        selectedPanosId.push(panorama.id);
      }
    });

    immersive3dTourTabStore.movePanoramasToFloor(selectedPanosId, targetItem.id);
    immersive3dTourTabStore.unselectAllPanoramas();
  }

  /**
   * @param {number} panoramaId
   * @param {MouseEvent} event
   */
  selectPanorama(panoramaId, event) {
    const {
      immersive3dTourAppPageStore: { immersive3dTourTabStore },
    } = this.props;

    const { floorsDisabled } = immersive3dTourTabStore;

    // if adding floors are disabled, then drag & drop
    if (floorsDisabled) {
      return;
    }

    // metKey is the CMD in Mac. Also it's the Windows button on Windows, but that button is not used for multi select,
    // we use CTRL instead. Also applies for keyboard in Linux.
    const isMetaKey = event.metaKey || event.ctrlKey;
    const isShiftKey = event.shiftKey;

    // We'll select panoramas taking into account if the meta key (CMD / CTRL) or the shift key are being pressed.
    const bothKeysPressed = isMetaKey && isShiftKey;
    const noneKeysPressed = !isMetaKey && !isShiftKey;

    if (bothKeysPressed || noneKeysPressed) {
      immersive3dTourTabStore.setPanoramaSelectedGreedy(panoramaId);
      return;
    }

    // If the CMD / CTRL key is pressed, then toggle the selection of the pano.
    if (isMetaKey) {
      immersive3dTourTabStore.togglePanoramaSelected(panoramaId);
      return;
    }

    if (isShiftKey) {
      immersive3dTourTabStore.spanPanoramaSelection(panoramaId);
    }
  }

  addSequentialFloor() {
    const {
      immersive3dTourAppPageStore: { immersive3dTourTabStore },
    } = this.props;

    immersive3dTourTabStore.addSequentialFloor();
  }

  updateFloorName(floorId, value) {
    const {
      immersive3dTourAppPageStore: { immersive3dTourTabStore },
    } = this.props;

    immersive3dTourTabStore.updateFloorNameById(floorId, value);
  }

  render() {
    const { immersive3dTourAppPageStore } = this.props;

    const { immersive3dTourStore, immersive3dTourTabStore, tourJobStore, productStore } = immersive3dTourAppPageStore;
    const { immersive3dTourPanoramasWithOrWithoutFloorPlanTourJob } = tourJobStore;

    const { immersive3dTour } = immersive3dTourStore;

    const {
      newPanoramasCount,
      floorsDisabled,
      hasWarnings,
      hasErrors,
      message,
      bucketFloor,
      floors,
      thumbnails,
      orphanPanoramasCount,
      unusedFloorsCount,
    } = immersive3dTourTabStore;

    return (
      <>
        <Grid>
          <Grid.Row>
            <Grid.Column width={8}>
              <h2 className="ui header">Edit your panoramas</h2>
            </Grid.Column>
            <Grid.Column width={8} textAlign="right">
              <ButtonGroup>
                <Button
                  secondary
                  onClick={() => {
                    immersive3dTourTabStore.forceShowUpload = true;
                  }}
                >
                  Upload more panoramas
                </Button>
                <ProcessingModal
                  hasWarnings={hasWarnings}
                  hasErrors={hasErrors}
                  panoramasCount={immersive3dTour.panoramasCount}
                  orphanPanoramasCount={orphanPanoramasCount}
                  unusedFloorsCount={unusedFloorsCount}
                  newPanoramasCount={newPanoramasCount}
                  immersive3dTour={immersive3dTour}
                  tourJob={immersive3dTourPanoramasWithOrWithoutFloorPlanTourJob}
                  immersive3dTourTabStore={immersive3dTourTabStore}
                  productDiscountedCreditCosts={productStore.productDiscountedCreditCosts}
                />
              </ButtonGroup>
            </Grid.Column>
          </Grid.Row>
          {(message || hasErrors || !floorsDisabled) && (
            <Grid.Row>
              <Grid.Column width={16}>
                {message && (
                  <Message
                    positive={message.type === "positive"}
                    negative={message.type === "negative"}
                    header={message.header}
                    content={message.content}
                  />
                )}
                {hasErrors && (
                  <Message negative>
                    <Message.Header>We&apos;re sorry, we cannot process your images</Message.Header>
                    <p>
                      One or multiple errors will prevent this Immersive 3D Tour from being successfully processed.
                      Please go back to the last step to fix these errors.
                    </p>
                  </Message>
                )}
                {!floorsDisabled && (
                  <Message warning>
                    <Message.Header>Important message</Message.Header>
                    <p>
                      Please group images by floor before processing (example: Main Floor, Second Floor, basement etc).
                      <br />
                      Create a new floor and drag the images to the appropriate section.
                    </p>
                  </Message>
                )}
              </Grid.Column>
            </Grid.Row>
          )}
          <Grid.Row className="editYourPanoramasHeader">
            <Grid.Column width={16}>
              <h3>Panoramas ({newPanoramasCount})</h3>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <div className="ui dividing header" />
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <CustomDndProvider>
          {/* Used as the bucket */}
          <Immersive3dTourFloor
            // force disabled on the bucket in order to trigger
            labelEditDisabled
            floorsDisabled={floorsDisabled}
            // Display as bucket floor but only if floors are enabled.
            isBucketFloor={!floorsDisabled}
            floor={bucketFloor}
            onPanoramaClick={this.selectPanorama}
            selectedPanoramasCount={immersive3dTourTabStore.selectedPanoramasCount}
            onPanoramaDragBeginCallback={this.onPanoramaDragBegin}
            onPanoramaDragEndCallback={(sourceItem, targetItem) => this.onPanoramaDragEnd(sourceItem, targetItem)}
            deletePanoramaCallback={panoramaId => immersive3dTourTabStore.deletePanoramaWithId(panoramaId)}
            openProcessingImageModalCallback={currentImageUrl => this.processingImageStore.openModal(currentImageUrl)}
            thumbnails={thumbnails}
            draggingPanoramaId={immersive3dTourTabStore.draggingPanoramaId}
          />
          <Immersive3dTourFloorList
            floorsDisabled={floorsDisabled}
            floors={floors}
            onFloorDelete={this.onFloorDelete}
            onFloorHoverCallback={(sourceItem, targetItem) => this.onFloorHover(sourceItem, targetItem)}
            floorNameChangeCallback={(floorId, value) => this.updateFloorName(floorId, value)}
            onPanoramaClick={this.selectPanorama}
            selectedPanoramasCount={immersive3dTourTabStore.selectedPanoramasCount}
            onPanoramaDragBeginCallback={this.onPanoramaDragBegin}
            onPanoramaDragEndCallback={(sourceItem, targetItem) => this.onPanoramaDragEnd(sourceItem, targetItem)}
            deletePanoramaCallback={panoramaId => immersive3dTourTabStore.deletePanoramaWithId(panoramaId)}
            openProcessingImageModalCallback={currentImageUrl => this.processingImageStore.openModal(currentImageUrl)}
            thumbnails={thumbnails}
            draggingPanoramaId={immersive3dTourTabStore.draggingPanoramaId}
          />
        </CustomDndProvider>
        {!floorsDisabled && (
          <Button secondary onClick={() => this.addSequentialFloor()}>
            Add Floor
          </Button>
        )}
        <ProcessingImageModal processingImageStore={this.processingImageStore} />
      </>
    );
  }
}

PanoramasSection.propTypes = {
  immersive3dTourAppPageStore: PropTypes.instanceOf(Immersive3dTourAppPageStore),
};

export default PanoramasSection;
